// src/marketing/channels/ChannelsBudgetPage.tsx
import React, { useState, useMemo } from 'react'
import { useAppStore } from '../../shell/appStore'
import { ApiClientError } from '../../lib/api'
import {
  useMarketingActions,
  useCreateMarketingAction,
  useUpdateMarketingAction,
  useDeleteMarketingAction,
} from './hooks/useMarketingActions'
import {
  useBudgetLines,
  useCreateBudgetLine,
  useUpdateBudgetLine,
  useDeleteBudgetLine,
} from './hooks/useBudgetLines'
import type { MarketingAction, BudgetLine, MarketingChannel } from '../../lib/types'
import { Badge } from '../editorial/Badge'
import { Chip } from '../editorial/Chip'
import { DailyExpensesSection } from '../budget/DailyExpensesSection'
import { useDailyExpenses } from '../budget/useDailyExpenses'
import { actualByCategory } from '../budget/aggregate'

const CHANNELS: { value: MarketingChannel | ''; label: string }[] = [
  { value: '', label: 'Tous les canaux' },
  { value: 'reddit', label: 'Reddit' },
  { value: 'product_hunt', label: 'Product Hunt' },
  { value: 'paid', label: 'Paid' },
  { value: 'email', label: 'Email' },
  { value: 'press', label: 'Presse' },
  { value: 'other', label: 'Autre' },
]

// Mapping canal → variante Badge éditoriale (.track-*)
// reddit / product_hunt → track-ugc (canaux communautaires)
// paid / email         → track-affilies (canaux commerciaux)
// press / other        → track-commun (communication générale)
const CHANNEL_LABELS: Record<string, string> = {
  reddit: 'Reddit',
  product_hunt: 'Product Hunt',
  paid: 'Paid',
  email: 'Email',
  press: 'Presse',
  other: 'Autre',
}

type BadgeVariant = 'track-ugc' | 'track-affilies' | 'track-commun'

function channelVariant(channel: string): BadgeVariant {
  switch (channel) {
    case 'reddit':
    case 'product_hunt':
      return 'track-ugc'
    case 'paid':
    case 'email':
      return 'track-affilies'
    default:
      return 'track-commun'
  }
}

// ── Formulaire d'action ────────────────────────────────────────────────────

type ActionForm = {
  channel: MarketingChannel
  title: string
  url: string
  notes: string
  actionDate: string
  cost: string
}

const EMPTY_ACTION_FORM: ActionForm = {
  channel: 'reddit',
  title: '',
  url: '',
  notes: '',
  actionDate: '',
  cost: '',
}

// ── Formulaire de ligne budget ─────────────────────────────────────────────

type BudgetForm = { category: string; label: string; planned: string }
const EMPTY_BUDGET_FORM: BudgetForm = { category: '', label: '', planned: '' }

// ── Composant principal ────────────────────────────────────────────────────

export function ChannelsBudgetPage() {
  const appId = useAppStore((s) => s.activeAppId)

  const [channelFilter, setChannelFilter] = useState<MarketingChannel | ''>('')
  const [toast, setToast] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [budgetError, setBudgetError] = useState<string | null>(null)

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 4000)
  }

  const { data: actions = [], isLoading: actionsLoading } = useMarketingActions(
    appId,
    channelFilter || undefined,
  )
  const { data: lines = [], isLoading: linesLoading } = useBudgetLines(appId)
  const { data: expenses = [] } = useDailyExpenses(appId)
  const computedActuals = useMemo(() => actualByCategory(expenses), [expenses])

  const createAction = useCreateMarketingAction()
  const deleteAction = useDeleteMarketingAction()
  const createLine = useCreateBudgetLine()
  const deleteLine = useDeleteBudgetLine()

  // ── Formulaire action ────────────────────────────────────────────────────
  const [showActionForm, setShowActionForm] = useState(false)
  const [actionForm, setActionForm] = useState<ActionForm>(EMPTY_ACTION_FORM)

  const [editActionId, setEditActionId] = useState<string | null>(null)
  const updateAction = useUpdateMarketingAction(editActionId ?? '')

  function startEditAction(action: MarketingAction) {
    setEditActionId(action.id)
    setActionForm({
      channel: action.channel,
      title: action.title,
      url: action.url ?? '',
      notes: action.notes ?? '',
      actionDate: action.actionDate ?? '',
      cost: action.cost != null ? String(action.cost) : '',
    })
    setShowActionForm(true)
  }

  function handleActionSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!appId) return
    setActionError(null)
    const payload = {
      appId,
      channel: actionForm.channel,
      title: actionForm.title,
      url: actionForm.url || null,
      notes: actionForm.notes || null,
      actionDate: actionForm.actionDate || null,
      cost: actionForm.cost !== '' ? parseFloat(actionForm.cost) : null,
    }
    if (editActionId) {
      updateAction.mutate(payload, {
        onSuccess: () => {
          setShowActionForm(false)
          setEditActionId(null)
          setActionForm(EMPTY_ACTION_FORM)
          showToast('Action enregistrée')
        },
        onError: (err) => {
          setActionError(err instanceof ApiClientError ? err.message : 'Erreur inattendue')
        },
      })
    } else {
      createAction.mutate(payload, {
        onSuccess: () => {
          setShowActionForm(false)
          setActionForm(EMPTY_ACTION_FORM)
          showToast('Action enregistrée')
        },
        onError: (err) => {
          setActionError(err instanceof ApiClientError ? err.message : 'Erreur inattendue')
        },
      })
    }
  }

  // ── Formulaire budget ────────────────────────────────────────────────────
  const [showBudgetForm, setShowBudgetForm] = useState(false)
  const [budgetForm, setBudgetForm] = useState<BudgetForm>(EMPTY_BUDGET_FORM)

  const [editLineId, setEditLineId] = useState<string | null>(null)
  const updateLine = useUpdateBudgetLine(editLineId ?? '')

  function startEditLine(line: BudgetLine) {
    setEditLineId(line.id)
    setBudgetForm({
      category: line.category,
      label: line.label ?? '',
      planned: String(line.planned),
    })
    setShowBudgetForm(true)
  }

  function handleBudgetSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!appId) return
    setBudgetError(null)
    const payload = {
      appId,
      category: budgetForm.category,
      label: budgetForm.label || null,
      planned: budgetForm.planned !== '' ? parseFloat(budgetForm.planned) : 0,
    }
    if (editLineId) {
      updateLine.mutate(payload, {
        onSuccess: () => {
          setShowBudgetForm(false)
          setEditLineId(null)
          setBudgetForm(EMPTY_BUDGET_FORM)
          showToast('Ligne de budget enregistrée')
        },
        onError: (err) => {
          setBudgetError(err instanceof ApiClientError ? err.message : 'Erreur inattendue')
        },
      })
    } else {
      createLine.mutate(payload, {
        onSuccess: () => {
          setShowBudgetForm(false)
          setBudgetForm(EMPTY_BUDGET_FORM)
          showToast('Ligne de budget enregistrée')
        },
        onError: (err) => {
          setBudgetError(err instanceof ApiClientError ? err.message : 'Erreur inattendue')
        },
      })
    }
  }

  // ── Calculs ──────────────────────────────────────────────────────────────
  const totalPlanned = useMemo(
    () => lines.reduce((sum, l) => sum + l.planned, 0),
    [lines],
  )
  const totalActual = useMemo(
    () =>
      [...new Set(lines.map((l) => l.category))].reduce(
        (sum, cat) => sum + (computedActuals.get(cat) ?? 0),
        0,
      ),
    [lines, computedActuals],
  )
  // Rollup : somme des coûts des actions (null ignorés)
  const loggedCost = useMemo(
    () => actions.reduce((sum, a) => sum + (a.cost ?? 0), 0),
    [actions],
  )

  // ── Rendu ────────────────────────────────────────────────────────────────
  return (
    <div
      data-testid="marketing-channels"
      style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem', padding: '1.5rem' }}
    >

      {/* ── Section Dépenses quotidiennes ───────────────────────────────── */}
      <DailyExpensesSection appId={appId} budgetLines={lines} />

      {/* ── Section Journal d'actions ───────────────────────────────────── */}
      <section data-testid="actions-section">
        {/* En-tête éditorial */}
        <div style={sectionHeadStyle}>
          <div>
            <p style={sectionLabelStyle}>Journal</p>
            <h2 style={sectionTitleStyle}>Actions marketing</h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <select
              data-testid="channel-filter"
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value as MarketingChannel | '')}
              style={{ ...inputStyle, width: 'auto' }}
            >
              {CHANNELS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <button
              data-testid="add-action-btn"
              onClick={() => {
                setEditActionId(null)
                setActionForm(EMPTY_ACTION_FORM)
                setShowActionForm(true)
              }}
              style={btnPrimary}
            >
              Loguer une action
            </button>
          </div>
        </div>

        {/* Formulaire création / édition action */}
        {showActionForm && (
          <form onSubmit={handleActionSubmit} style={formStyle}>
            {actionError && (
              <div
                data-testid="action-error"
                style={{ gridColumn: '1 / -1', color: 'var(--arancio)', fontSize: '0.8125rem', padding: '0.5rem 0.75rem', background: 'var(--arancio-10, #fff0eb)', borderRadius: '6px' }}
              >
                {actionError}
              </div>
            )}
            <div>
              <label style={labelStyle} htmlFor="action-channel">Canal</label>
              <select
                id="action-channel"
                value={actionForm.channel}
                onChange={(e) => setActionForm((f) => ({ ...f, channel: e.target.value as MarketingChannel }))}
                style={inputStyle}
              >
                {CHANNELS.filter((c) => c.value !== '').map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={labelStyle} htmlFor="action-title">Titre</label>
              <input
                id="action-title"
                value={actionForm.title}
                onChange={(e) => setActionForm((f) => ({ ...f, title: e.target.value }))}
                required
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle} htmlFor="action-date">Date</label>
              <input
                id="action-date"
                type="date"
                value={actionForm.actionDate}
                onChange={(e) => setActionForm((f) => ({ ...f, actionDate: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle} htmlFor="action-cost">Coût (€)</label>
              <input
                id="action-cost"
                type="number"
                min="0"
                step="0.01"
                value={actionForm.cost}
                onChange={(e) => setActionForm((f) => ({ ...f, cost: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle} htmlFor="action-url">URL</label>
              <input
                id="action-url"
                type="url"
                value={actionForm.url}
                onChange={(e) => setActionForm((f) => ({ ...f, url: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem' }}>
              <button type="submit" style={btnPrimary}>
                {editActionId ? 'Enregistrer' : 'Créer'}
              </button>
              <button
                type="button"
                onClick={() => { setShowActionForm(false); setEditActionId(null) }}
                style={btnSecondary}
              >
                Annuler
              </button>
            </div>
          </form>
        )}

        {/* Tableau des actions — .twrap */}
        <div className="twrap">
          {actionsLoading ? (
            <p style={{ padding: '1rem', color: 'var(--muted)', fontSize: '0.875rem' }}>
              Chargement…
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Canal</th>
                  <th style={thStyle}>Titre</th>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Coût</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {actions.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ ...tdStyle, color: 'var(--muted)', textAlign: 'center' }}>
                      Aucune action enregistrée pour ce canal.
                    </td>
                  </tr>
                )}
                {actions.map((action) => (
                  <tr
                    key={action.id}
                    data-testid={`action-item-${action.id}`}
                    style={{ borderTop: '1px solid var(--line)' }}
                  >
                    <td style={tdStyle}>
                      <Badge variant={channelVariant(action.channel)}>
                        {CHANNEL_LABELS[action.channel] ?? action.channel}
                      </Badge>
                    </td>
                    <td style={{ ...tdStyle, fontWeight: 500 }}>{action.title}</td>
                    <td style={tdStyle}>
                      {action.actionDate
                        ? <Chip variant="date">{action.actionDate}</Chip>
                        : <span style={{ color: 'var(--muted)' }}>—</span>
                      }
                    </td>
                    <td style={tdStyle}>
                      {action.cost != null
                        ? <Chip>{action.cost.toFixed(2)} {action.currency}</Chip>
                        : <span style={{ color: 'var(--muted)' }}>—</span>
                      }
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                        <button onClick={() => startEditAction(action)} style={btnIconStyle}>Modifier</button>
                        <button
                          onClick={() => {
                            if (!window.confirm('Supprimer cette action ?')) return
                            deleteAction.mutate(action.id, {
                              onSuccess: () => showToast('Action supprimée'),
                              onError: (err) => showToast(err instanceof ApiClientError ? err.message : 'Erreur inattendue'),
                            })
                          }}
                          style={{ ...btnIconStyle, color: 'var(--arancio)' }}
                        >
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* ── Section Tableau budget ─────────────────────────────────────────── */}
      <section data-testid="budget-section">
        {/* En-tête éditorial */}
        <div style={sectionHeadStyle}>
          <div>
            <p style={sectionLabelStyle}>Budget</p>
            <h2 style={sectionTitleStyle}>Budget marketing</h2>
          </div>
          <button
            data-testid="add-budget-line-btn"
            onClick={() => {
              setEditLineId(null)
              setBudgetForm(EMPTY_BUDGET_FORM)
              setShowBudgetForm(true)
            }}
            style={btnPrimary}
          >
            Ajouter une ligne
          </button>
        </div>

        {/* Formulaire ligne budget */}
        {showBudgetForm && (
          <form onSubmit={handleBudgetSubmit} style={{ ...formStyle, display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {budgetError && (
              <div
                data-testid="budget-error"
                style={{ width: '100%', color: 'var(--arancio)', fontSize: '0.8125rem', padding: '0.5rem 0.75rem', background: 'var(--arancio-10, #fff0eb)', borderRadius: '6px' }}
              >
                {budgetError}
              </div>
            )}
            <div>
              <label style={labelStyle} htmlFor="budget-category">Catégorie</label>
              <input
                id="budget-category"
                value={budgetForm.category}
                onChange={(e) => setBudgetForm((f) => ({ ...f, category: e.target.value }))}
                required
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle} htmlFor="budget-label">Label (optionnel)</label>
              <input
                id="budget-label"
                value={budgetForm.label}
                onChange={(e) => setBudgetForm((f) => ({ ...f, label: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle} htmlFor="budget-planned">Prévu (€)</label>
              <input
                id="budget-planned"
                type="number"
                min="0"
                step="0.01"
                value={budgetForm.planned}
                onChange={(e) => setBudgetForm((f) => ({ ...f, planned: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="submit" style={btnPrimary}>
                {editLineId ? 'Enregistrer' : 'Ajouter'}
              </button>
              <button
                type="button"
                onClick={() => { setShowBudgetForm(false); setEditLineId(null) }}
                style={btnSecondary}
              >
                Annuler
              </button>
            </div>
          </form>
        )}

        {/* Tableau budget — .twrap */}
        <div className="twrap">
          {linesLoading ? (
            <p style={{ padding: '1rem', color: 'var(--muted)', fontSize: '0.875rem' }}>Chargement…</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Catégorie</th>
                  <th style={thStyle}>Label</th>
                  <th style={thStyle}>Prévu (€)</th>
                  <th style={thStyle}>Réel calculé (€)</th>
                  <th style={thStyle}>Écart (€)</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ ...tdStyle, color: 'var(--muted)', textAlign: 'center' }}>
                      Aucune ligne de budget.
                    </td>
                  </tr>
                )}
                {lines.map((line) =>
                  editLineId === line.id ? (
                    <tr key={line.id} data-testid={`budget-row-${line.id}`} style={{ borderTop: '1px solid var(--line)', background: 'var(--elevated)' }}>
                      <td style={tdStyle} colSpan={5}>
                        <em style={{ color: 'var(--muted)', fontSize: '0.8125rem' }}>Édition en cours dans le formulaire ci-dessus…</em>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <button onClick={() => { setShowBudgetForm(false); setEditLineId(null) }} style={btnIconStyle}>
                          Annuler
                        </button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={line.id} data-testid={`budget-row-${line.id}`} style={{ borderTop: '1px solid var(--line)' }}>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{line.category}</td>
                      <td style={{ ...tdStyle, color: 'var(--muted)' }}>{line.label ?? '—'}</td>
                      <td style={tdStyle}>{line.planned.toFixed(2)}</td>
                      <td style={tdStyle} data-testid={`budget-actual-${line.id}`}>
                        {(computedActuals.get(line.category) ?? 0).toFixed(2)}
                      </td>
                      <td style={{ ...tdStyle, color: line.planned - (computedActuals.get(line.category) ?? 0) < 0 ? 'var(--arancio)' : 'var(--muted)' }}>
                        {(line.planned - (computedActuals.get(line.category) ?? 0)).toFixed(2)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <button onClick={() => startEditLine(line)} style={btnIconStyle}>Modifier</button>
                        <button
                          onClick={() => {
                            if (!window.confirm('Supprimer cette ligne de budget ?')) return
                            deleteLine.mutate(line.id, {
                              onSuccess: () => showToast('Ligne de budget supprimée'),
                              onError: (err) => showToast(err instanceof ApiClientError ? err.message : 'Erreur inattendue'),
                            })
                          }}
                          style={{ ...btnIconStyle, color: 'var(--arancio)', marginLeft: '0.5rem' }}
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--line)', fontWeight: 700 }}>
                  <td style={tdStyle} colSpan={2}>Totaux</td>
                  <td data-testid="budget-total-planned" style={tdStyle}>{totalPlanned.toFixed(2)}</td>
                  <td data-testid="budget-total-actual" style={tdStyle}>{totalActual.toFixed(2)}</td>
                  <td
                    data-testid="budget-total-gap"
                    style={{ ...tdStyle, color: totalPlanned - totalActual < 0 ? 'var(--arancio)' : 'var(--muted)' }}
                  >
                    {(totalPlanned - totalActual).toFixed(2)}
                  </td>
                  <td style={tdStyle} />
                </tr>
                <tr style={{ borderTop: '1px solid var(--line)', color: 'var(--muted)' }}>
                  <td style={tdStyle} colSpan={2}>Dépenses loguées (actions)</td>
                  <td style={tdStyle} />
                  <td data-testid="budget-logged-cost" style={tdStyle}>{loggedCost.toFixed(2)}</td>
                  <td style={tdStyle} />
                  <td style={tdStyle} />
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </section>

      {/* ── Toast ──────────────────────────────────────────────────────────── */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            right: '1.5rem',
            background: 'var(--elevated)',
            border: '1px solid var(--line)',
            borderRadius: '8px',
            padding: '0.625rem 1rem',
            fontSize: '0.875rem',
            color: 'var(--fg)',
            zIndex: 2000,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  )
}

// ── Styles ────────────────────────────────────────────────────────────────────

const sectionHeadStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '0.75rem',
  flexWrap: 'wrap',
  marginBottom: '0.75rem',
}

const sectionLabelStyle: React.CSSProperties = {
  margin: 0,
  fontFamily: 'var(--mono)',
  fontSize: '10px',
  letterSpacing: '.2em',
  textTransform: 'uppercase',
  color: 'var(--bronze)',
}

const sectionTitleStyle: React.CSSProperties = {
  margin: '4px 0 0',
  fontWeight: 800,
  fontSize: '1.1rem',
  textTransform: 'uppercase',
  letterSpacing: '.01em',
}

const formStyle: React.CSSProperties = {
  padding: '0.875rem 0',
  marginBottom: '0.75rem',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
  gap: '0.75rem',
}

const inputStyle: React.CSSProperties = {
  background: 'var(--bg)',
  border: '1px solid var(--line)',
  borderRadius: '6px',
  padding: '0.375rem 0.625rem',
  fontSize: '0.875rem',
  color: 'var(--fg)',
  fontFamily: 'inherit',
  width: '100%',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.75rem',
  color: 'var(--muted)',
  marginBottom: '0.25rem',
}

const thStyle: React.CSSProperties = {
  fontFamily: 'var(--mono)',
  fontSize: '10.5px',
  letterSpacing: '.1em',
  textTransform: 'uppercase',
  color: 'var(--bronze)',
  textAlign: 'left',
  padding: '11px 13px',
  background: 'var(--surface)',
  borderBottom: '1px solid var(--line)',
  whiteSpace: 'nowrap',
}

const tdStyle: React.CSSProperties = {
  padding: '10px 13px',
  color: 'var(--fg)',
  verticalAlign: 'top',
}

const btnPrimary: React.CSSProperties = {
  background: 'var(--arancio)',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  padding: '0.4375rem 1rem',
  fontSize: '0.875rem',
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: 'pointer',
}

const btnSecondary: React.CSSProperties = {
  background: 'var(--elevated)',
  color: 'var(--fg)',
  border: '1px solid var(--line)',
  borderRadius: '6px',
  padding: '0.4375rem 1rem',
  fontSize: '0.875rem',
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: 'pointer',
}

const btnIconStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--arancio)',
  cursor: 'pointer',
  fontSize: '0.8125rem',
  fontFamily: 'inherit',
  padding: '0.125rem 0.25rem',
}
